export type Role = 'admin' | 'member';

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  role: Role;
  department: string;
  mustChangePassword: boolean;
};

export type Department = {
  id: string;
  name: string;
  slug: string;
  summary: string;
  sort_order: number;
};

export type Application = {
  id: string;
  department_id: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  audience: string;
  status: 'live' | 'beta' | 'planned' | 'maintenance';
  url: string | null;
  stack: string;
  version: string;
  sort_order: number;
};

export type Feature = {
  id: string;
  application_id: string;
  title: string;
  detail: string;
  sort_order: number;
};

export type AppRequest = {
  id: string;
  requester_id: string;
  title: string;
  department: string;
  description: string;
  preferred_date: string | null;
  status: string;
  admin_notes: string;
  appointment_at: string | null;
  created_at: string;
  updated_at: string;
  requester_name?: string;
  requester_email?: string;
};

export type ServiceStatus = {
  id: string;
  application_id: string | null;
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance';
  message: string;
  updated_at: string;
  updated_by: string | null;
};

export type Incident = {
  id: string;
  service_id: string;
  title: string;
  body: string;
  severity: string;
  started_at: string;
  resolved_at: string | null;
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'same-origin',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || `Request failed (${res.status})`);
  }
  return data as T;
}

export const client = {
  me: () => api<{ user: SessionUser | null; guest?: boolean }>('/auth/me'),
  login: (email: string, password: string) =>
    api<{ user: SessionUser }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => api<{ ok: boolean }>('/auth/logout', { method: 'POST', body: '{}' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ ok: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  catalog: () =>
    api<{ departments: Department[]; applications: Application[]; features: Feature[] }>('/catalog'),
  appDetail: (slug: string) =>
    api<{ application: Application; department: Department; features: Feature[] }>(`/catalog/apps/${slug}`),
  requests: () => api<{ requests: AppRequest[] }>('/requests'),
  createRequest: (body: {
    title: string;
    department?: string;
    description: string;
    preferredDate?: string | null;
  }) => api<{ request: AppRequest }>('/requests', { method: 'POST', body: JSON.stringify(body) }),
  updateRequest: (
    id: string,
    body: { status?: string; adminNotes?: string; appointmentAt?: string | null },
  ) => api<{ request: AppRequest }>(`/requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  status: () => api<{ services: ServiceStatus[]; incidents: Incident[] }>('/status'),
  updateService: (id: string, body: { status: string; message?: string }) =>
    api<{ service: ServiceStatus }>(`/status/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addIncident: (serviceId: string, body: { title: string; body?: string; severity?: string }) =>
    api<{ incident: Incident }>(`/status/${serviceId}/incidents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolveIncident: (id: string) =>
    api<{ ok: boolean }>(`/status/incidents/${id}/resolve`, { method: 'POST', body: '{}' }),
  users: () =>
    api<{
      users: Array<{
        id: string;
        email: string;
        name: string;
        role: Role;
        department: string;
        active: boolean;
        mustChangePassword: boolean;
        createdAt: string;
        lastLoginAt: string | null;
      }>;
    }>('/admin/users'),
  createUser: (body: { email: string; name: string; role?: Role; department?: string }) =>
    api<{ user: { id: string; email: string; name: string; role: Role }; temporaryPassword: string }>(
      '/admin/users',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateUser: (id: string, body: { name?: string; role?: Role; active?: boolean; department?: string }) =>
    api<{ user: unknown }>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  resetPassword: (id: string) =>
    api<{ temporaryPassword: string }>(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: '{}',
    }),
};
